import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
    mapToHomeLoanEnquiryDto,
    mapToGeneralEnquiryDto,
    mapToPersonalLoanDto,
    mapToHardshipDto,
    mapToFinancialPlanningDto,
} from '../Integrations/mapper';
import { thunkHandler } from '../utils/utilFunctions';
import type { AddressState, ApplicantsState, ClientDetails } from './types';

/* Async Actions */
export const getClientDetails = createAsyncThunk(
    "",
    async (_,) => {
        return axios.request<ClientDetails>({
            url: import.meta.env.VITE_ClientDetailsAPI,
        }).then((response) => {
            return response.data;
        }).catch((error) => {
            console.error(error);
            const response: ClientDetails = {
                IPAddress: ""
            };
            return response;
        });
    }
);

export const submitHomeLoanEnquiry = createAsyncThunk<
    any,
    { currentApplicantIndex: number, clientDetails: ClientDetails }
>(
    'HomeLoanReferral/v1',
    async (data, { getState, rejectWithValue }) => {
        const { currentApplicantIndex } = data;
        const { clientDetails } = data;
        const { applicants } = getState() as {
            applicants: ApplicantsState;
        };

        const dto = mapToHomeLoanEnquiryDto(
            applicants.applicants[currentApplicantIndex],
            clientDetails.IPAddress
        );

        return thunkHandler(
            axios({
                method: 'post',
                url: import.meta.env.VITE_HomeLoanReferralAPI,
                headers: {
                    'OCP-Apim-subscription-key': import.meta.env.VITE_HomeLoanReferralAPI_SubscriptionKey
                },
                data: {
                    correlationId: dto.correlationId,
                    originationBrand: dto.originationBrand,
                    originationSystem: dto.originationSystem,
                    loanPurpose: dto.loanPurpose,
                    loanType: dto.loanType,
                    loanAmount: dto.loanAmount,
                    additionalFunds: dto.additionalFunds,
                    comments: dto.comments,
                    givenName: dto.givenName,
                    familyName: dto.familyName,
                    homePhone: dto.homePhone,
                    workPhone: dto.workPhone,
                    mobilePhone: dto.mobilePhone,
                    email: dto.email,
                    employmentStatus: dto.employmentStatus,
                    isExistingMember: dto.isExistingMember,
                    privacyConsent: dto.privacyConsent,
                    preferredContactMethod: dto.preferredContactMethod,
                    callerDetails: {
                        ipAddress: dto.callerDetails?.ipAddress,
                        deviceId: dto.callerDetails?.deviceId,
                        userAgentString: dto.callerDetails?.userAgentString,
                        sessionId: dto.callerDetails?.sessionId
                    }
                }
            }),
            rejectWithValue
        );
    }
);

export const submitGeneralEnquiry = createAsyncThunk<
    any,
    { currentApplicantIndex: number, clientDetails: ClientDetails }
>(
    'GeneralEnquiry/v1',
    async (data, { getState, rejectWithValue }) => {
        const { currentApplicantIndex } = data;
        const { clientDetails } = data;
        const { applicants } = getState() as {
            applicants: ApplicantsState;
        };

        const dto = mapToGeneralEnquiryDto(
            applicants.applicants[currentApplicantIndex],
            clientDetails.IPAddress
        );

        return thunkHandler(
            axios({
                method: 'post',
                url: import.meta.env.VITE_EnquiryAPI,
                headers: {
                    'OCP-Apim-subscription-key': import.meta.env.VITE_EnquiryAPI_SubscriptionKey
                },
                data: {
                    correlationId: dto.correlationId,
                    originationBrand: dto.originationBrand,
                    originationSystem: dto.originationSystem,
                    enquiryFormType: 'GeneralEnquiry',
                    firstName: dto.firstName,
                    lastName: dto.lastName,
                    mobileNumber: dto.mobileNumber,
                    emailAddress: dto.emailAddress,
                    preferredContactMethod: dto.preferredContactMethod,
                    isExistingCustomer: dto.isExistingCustomer,
                    privacyConsent: dto.privacyConsent,
                    generalMessage: dto.generalMessage,
                    callerDetails: {
                        ipAddress: dto.callerDetails?.ipAddress,
                        deviceId: dto.callerDetails?.deviceId,
                        userAgentString: dto.callerDetails?.userAgentString,
                        sessionId: dto.callerDetails?.sessionId
                    }
                }
            }),
            rejectWithValue
        );
    }
);

export const submitFinancialPlanning = createAsyncThunk<
    any,
    { currentApplicantIndex: number, clientDetails: ClientDetails }
>(
    'FinancialPlanning/v1',
    async (data, { getState, rejectWithValue }) => {
        const { currentApplicantIndex } = data;
        const { clientDetails } = data;
        const { applicants } = getState() as {
            applicants: ApplicantsState;
        };

        const dto = mapToFinancialPlanningDto(
            applicants.applicants[currentApplicantIndex],
            clientDetails.IPAddress
        );

        return thunkHandler(
            axios({
                method: 'post',
                url: import.meta.env.VITE_EnquiryAPI,
                headers: {
                    'OCP-Apim-subscription-key': import.meta.env.VITE_EnquiryAPI_SubscriptionKey
                },
                data: {
                    correlationId: dto.correlationId,
                    originationBrand: dto.originationBrand,
                    originationSystem: dto.originationSystem,
                    enquiryFormType: 'FinancialPlanningEnquiry',
                    firstName: dto.firstName,
                    lastName: dto.lastName,
                    mobileNumber: dto.mobileNumber,
                    emailAddress: dto.emailAddress,
                    preferredContactMethod: dto.preferredContactMethod,
                    isExistingCustomer: dto.isExistingCustomer,
                    customerId: dto.customerId,
                    residentialAddress: dto.residentialAddress,
                    ageRange: dto.ageRange,
                    enquiryType: dto.enquiryType,
                    generalMessage: dto.generalMessage,
                    privacyConsent: dto.privacyConsent,
                    callerDetails: {
                        ipAddress: dto.callerDetails?.ipAddress,
                        deviceId: dto.callerDetails?.deviceId,
                        userAgentString: dto.callerDetails?.userAgentString,
                        sessionId: dto.callerDetails?.sessionId
                    }
                }
            }),
            rejectWithValue
        );
    }
);

export const submitHardship = createAsyncThunk<
    any,
    { currentApplicantIndex: number, clientDetails: ClientDetails }
>(
    'Hardship/v1',
    async (data, { getState, rejectWithValue }) => {
        const { currentApplicantIndex } = data;
        const { clientDetails } = data;
        const { applicants } = getState() as {
            applicants: ApplicantsState;
        };

        const dto = mapToHardshipDto(
            applicants.applicants[currentApplicantIndex],
            clientDetails.IPAddress
        );

        return thunkHandler(
            axios({
                method: 'post',
                url: import.meta.env.VITE_EnquiryAPI,
                headers: {
                    'OCP-Apim-subscription-key': import.meta.env.VITE_EnquiryAPI_SubscriptionKey
                },
                data: {
                    correlationId: dto.correlationId,
                    originationBrand: dto.originationBrand,
                    originationSystem: dto.originationSystem,
                    enquiryFormType: 'HardshipEnquiry',
                    firstName: dto.firstName,
                    lastName: dto.lastName,
                    mobileNumber: dto.mobileNumber,
                    emailAddress: dto.emailAddress,
                    generalMessage: dto.generalMessage,
                    privacyConsent: dto.privacyConsent,
                    callerDetails: {
                        ipAddress: dto.callerDetails?.ipAddress,
                        deviceId: dto.callerDetails?.deviceId,
                        userAgentString: dto.callerDetails?.userAgentString,
                        sessionId: dto.callerDetails?.sessionId
                    }
                }
            }),
            rejectWithValue
        );
    }
);

export const submitPersonalLoanEnquiry = createAsyncThunk<
    any,
    { currentApplicantIndex: number, clientDetails: ClientDetails }
>(
    'PersonalLoan/v1',
    async (data, { getState, rejectWithValue }) => {
        const { currentApplicantIndex } = data;
        const { clientDetails } = data;
        const { applicants } = getState() as {
            applicants: ApplicantsState;
        };

        const dto = mapToPersonalLoanDto(
            applicants.applicants[currentApplicantIndex],
            clientDetails.IPAddress
        );

        return thunkHandler(
            axios({
                method: 'post',
                url: import.meta.env.VITE_EnquiryAPI,
                headers: {
                    'OCP-Apim-subscription-key': import.meta.env.VITE_EnquiryAPI_SubscriptionKey
                },
                data: {
                    correlationId: dto.correlationId,
                    originationBrand: dto.originationBrand,
                    originationSystem: dto.originationSystem,
                    enquiryFormType: 'PersonalLoanEnquiry',
                    firstName: dto.firstName,
                    lastName: dto.lastName,
                    emailAddress: dto.emailAddress,
                    mobileNumber: dto.mobileNumber,
                    isExistingCustomer: dto.isExistingCustomer,
                    customerId: dto.customerId,
                    residentialAddress: dto.residentialAddress,
                    loanAmount: dto.loanAmount,
                    loanPurpose: dto.loanPurpose,
                    housingSituation: dto.housingSituation,
                    hasExistingHomeLoan: dto.hasExistingHomeLoan,
                    grossSalary: dto.grossSalary,
                    employmentStatus: dto.employmentStatus,
                    callerDetails: {
                        ipAddress: dto.callerDetails?.ipAddress,
                        deviceId: dto.callerDetails?.deviceId,
                        userAgentString: dto.callerDetails?.userAgentString,
                        sessionId: dto.callerDetails?.sessionId
                    }
                }
            }),
            rejectWithValue
        );
    }
);

let addressSearchCancelTokenSource;

export const addressSearch = async (
    address: string,
    enquiryFormType: string,
    poBox?: boolean
): Promise<[]> => {
    // Cancel previous axios call
    if (typeof addressSearchCancelTokenSource !== typeof undefined) {
        addressSearchCancelTokenSource.cancel(
            'Address search request canceled due to new request.'
        );
    }

    addressSearchCancelTokenSource = axios.CancelToken.source();

    const response = await axios({
        method: 'get',
        url:
            import.meta.env.VITE_API +
            '/address/search?address=' + address +
            '&accountType=' + enquiryFormType +
            (poBox ? '&includePoBox=true' : ''),
        cancelToken: addressSearchCancelTokenSource.token,
    }).catch((error) => {
        if (axios.isCancel(error)) {
            return { data: [] }; // Not return error when canceled
        } else {
            return error;
        }
    });

    return response.data;
};

export const formatAddress = async (moniker: string): Promise<AddressState> => {
    const response = await axios({
        method: 'get',
        url:
            import.meta.env.VITE_API +
            '/address/formatted-address?moniker=' +
            encodeURIComponent(moniker),
    });

    return response.data;
};

export const getMemberDetails = createAsyncThunk<
    any,
    { customerId: string; applicantIndex: number }
>('applicants/getMemberDetails', async (data, { rejectWithValue }) => {
    const { customerId, applicantIndex } = data;

    return thunkHandler(
        (async (): Promise<any> => {
            try {
                const response = await axios({
                    method: 'post',
                    url:
                        import.meta.env.VITE_API + '/existing-member/get-member-details',
                    data: {
                        customerId: customerId,
                    },
                });

                response.data.applicantIndex = applicantIndex;
                return response;
            } catch (error) {
                throw error;
            }
        })(),
        rejectWithValue
    );
});